import { Meeting } from '@consigli/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatMeetingDate, formatMeetingTime } from './format-meeting-datetime';
import { MeetingCard } from './meeting-secretary-card';
import { MeetingSeriesCard } from './meeting-series-card';
import { MeetingSeries } from './use-grouped-meetings';
import { getNonOrganizerParticipants } from './utils/export-types';
import { getTranscriptStatus, hasPendingTranscripts } from './utils/transcript-status';

interface MeetingSeriesAccordionProps {
  series: MeetingSeries;
  onMeetingSelect?: (meeting: Meeting) => void;
  onDeleteSeries: (series: MeetingSeries) => void;
}

export const MeetingSeriesAccordion = ({
  series,
  onMeetingSelect,
  onDeleteSeries,
}: MeetingSeriesAccordionProps) => {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const nextMeetingLabel =
    series.nextMeeting &&
    `${formatMeetingDate(series.nextMeeting.scheduledStartTime, i18n.language)} ${t('meeting-secretary.meetings.at')} ${formatMeetingTime(series.nextMeeting, i18n.language).split(' (')[0]}`;
  const lastMeetingLabel =
    series.lastMeeting && !series.nextMeeting
      ? formatMeetingDate(series.lastMeeting.scheduledStartTime, i18n.language)
      : undefined;

  const handleDeleteSeries = () => {
    onDeleteSeries(series);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <MeetingSeriesCard
        title={series.title}
        meetingCount={series.meetings.length}
        nextMeetingLabel={nextMeetingLabel ?? undefined}
        lastMeetingLabel={lastMeetingLabel ?? undefined}
        isExpanded={isExpanded}
        hasPendingTranscripts={hasPendingTranscripts(series.meetings)}
        onExpandToggle={() => setIsExpanded(!isExpanded)}
        onDeleteSeries={handleDeleteSeries}
      />

      {/* Series Meetings */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="p-4 space-y-3">
            {series.meetings.map((meeting) => (
              <MeetingCard
                key={meeting.id}
                meetingId={meeting.id}
                title={meeting.title}
                company={meeting.organizerEmail}
                date={formatMeetingDate(meeting.scheduledStartTime, i18n.language)}
                time={formatMeetingTime(
                  meeting,
                  i18n.language,
                  t('meeting-secretary.meetings.minutes'),
                )}
                participants={t('meeting-secretary.meetings.participants-count', {
                  count: getNonOrganizerParticipants(meeting.participants).length,
                })}
                onSelect={() => onMeetingSelect?.(meeting)}
                isRecurring={!!meeting.recurringSeriesId}
                transcriptStatus={getTranscriptStatus(meeting)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
